import React, { useState } from 'react';
import PandaLoader from '../components/PandaLoader';
import LoadingScreen from '../components/LoadingScreen';
import LoadingAnimation from '../components/onboarding/LoadingAnimation';
import AudiobookProgressBar from '../components/audiobook/AudiobookProgressBar';

const Section = ({ title, description, children, background = '#FFFFFF' }) => (
  <section
    style={{
      background,
      border: '2px solid rgba(11,26,51,0.06)',
      borderRadius: 22,
      padding: 24,
      marginBottom: 24,
      boxShadow: '0px 2px 0px rgba(11,26,51,0.04), 0px 8px 20px rgba(11,26,51,0.06)',
    }}
  >
    <h2
      style={{
        fontFamily: "'Fredoka', sans-serif",
        color: '#0065F8',
        fontSize: 14,
        fontWeight: 800,
        textTransform: 'uppercase',
        letterSpacing: '0.12em',
        margin: '0 0 6px',
      }}
    >
      {title}
    </h2>
    <p
      style={{
        fontFamily: "'Fredoka', sans-serif",
        color: '#5A6B85',
        fontSize: 13,
        margin: '0 0 18px',
      }}
    >
      {description}
    </p>
    {children}
  </section>
);

const PreviewBox = ({ background, children, height = 360 }) => (
  <div
    style={{
      width: '100%',
      height,
      background,
      borderRadius: 16,
      overflow: 'hidden',
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: '1px dashed rgba(11,26,51,0.15)',
    }}
  >
    {children}
  </div>
);

const TestLoaders = () => {
  const [showFullscreen, setShowFullscreen] = useState(false);

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #F5FAFF 0%, #EAF3FF 100%)',
        padding: '120px 24px 60px',
        fontFamily: "'Inter', 'Fredoka', sans-serif",
      }}
    >
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <h1
          style={{
            fontFamily: "'Fredoka', sans-serif",
            color: '#0B1A33',
            fontSize: 'clamp(2rem, 4vw, 3rem)',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '-0.01em',
            margin: '0 0 8px',
          }}
        >
          Loader Test Page
        </h1>
        <p
          style={{
            fontFamily: "'Fredoka', sans-serif",
            color: '#5A6B85',
            fontSize: 15,
            margin: '0 0 32px',
          }}
        >
          Preview every loading state at <code>/test-loaders</code>. Delete this route before shipping.
        </p>

        <Section
          title="PandaLoader · inline (180px)"
          description="Embedded loader used in cards, modals, onboarding steps, and audiobook progress bars."
        >
          <PreviewBox
            background="linear-gradient(180deg, #F5FAFF 0%, #EAF3FF 100%)"
            height={260}
          >
            <PandaLoader variant="inline" size={180} />
          </PreviewBox>
        </Section>

        <Section
          title="PandaLoader · page variant"
          description="Fills a parent route view. Used by CharacterCreatingPlaceholder (full mode)."
        >
          <PreviewBox
            background="linear-gradient(180deg, #F5FAFF 0%, #EAF3FF 100%)"
            height={420}
          >
            <PandaLoader
              variant="page"
              message="Your character is being created"
              subMessage="Hang tight — this usually takes about 30 seconds."
            />
          </PreviewBox>
        </Section>

        <Section
          title="LoadingScreen wrapper (fullscreen overlay)"
          description="Used by CreateStory, character/episode/demo flows. Click the button to open the overlay over this page; click anywhere on it to close."
        >
          <button
            onClick={() => setShowFullscreen(true)}
            style={{
              fontFamily: "'Fredoka', sans-serif",
              background: '#0065F8',
              color: '#FFFFFF',
              fontWeight: 700,
              fontSize: 14,
              padding: '10px 20px',
              border: 'none',
              borderRadius: 999,
              boxShadow: '0px 4px 0px #073B8E',
              cursor: 'pointer',
            }}
          >
            Show fullscreen LoadingScreen
          </button>
          {showFullscreen && (
            <div onClick={() => setShowFullscreen(false)}>
              <LoadingScreen message="Creating your story..." subMessage="This usually takes 1-2 minutes" />
            </div>
          )}
        </Section>

        <Section
          title="Onboarding LoadingAnimation"
          description="Full panel with progress bar + rotating fun facts."
        >
          <PreviewBox
            background="linear-gradient(180deg, #F5FAFF 0%, #EAF3FF 100%)"
            height={560}
          >
            <LoadingAnimation />
          </PreviewBox>
        </Section>

        <Section
          title="AudiobookProgressBar"
          description="Used during audiobook generation. Progress bar driven by the parent."
        >
          <PreviewBox
            background="linear-gradient(180deg, #F5FAFF 0%, #EAF3FF 100%)"
            height={420}
          >
            <div style={{ width: '100%', maxWidth: 640, padding: 16 }}>
              <AudiobookProgressBar progress={42} />
            </div>
          </PreviewBox>
        </Section>

        <Section
          title="HTML splash (initial post-login)"
          description="Defined in public/index.html. Reload the page (Cmd+Shift+R) to see it before React mounts."
        >
          <p style={{ fontFamily: "'Fredoka', sans-serif", color: '#5A6B85', fontSize: 13, margin: 0 }}>
            This one can't be embedded — it lives outside React and only renders on the initial page load.
          </p>
        </Section>
      </div>
    </div>
  );
};

export default TestLoaders;
